import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, Menu, User as UserIcon, X } from 'lucide-react';
import { Button, Drawer, LangSwitch, Logo } from '../../../../components';
import { useLang } from '../../../../i18n';
import { useLeadModal } from '../../../../contexts/LeadModalContext';
import { useAuth } from '../../../../contexts/AuthContext';
import './Header.scss';

export default function Header() {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useLeadModal();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/services', label: t('nav.services') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/about', label: t('nav.about') },
    { to: '/contacts', label: t('nav.contacts') },
  ];

  const handleCta = () => {
    setOpen(false);
    openModal();
  };

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
      <div className="container header__inner">
        <div className="header__lead">
          <Link to="/" className="header__brand" aria-label={t('brand.name')}>
            <Logo variant="mark-text" size={32} />
          </Link>
          <nav className="header__nav" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `header__nav-link${isActive ? ' header__nav-link--active' : ''}`
                }
              >
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="header__actions">
          <LangSwitch />
          {user ? (
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate('/cabinet')}
              iconLeft={<UserIcon size={16} />}
            >
              {t('cta.cabinet')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate('/login')}
              iconLeft={<LogIn size={16} />}
            >
              {t('cta.login')}
            </Button>
          )}
          <Button variant="primary" size="md" onClick={handleCta}>
            {t('cta.request')}
          </Button>
        </div>
        <button
          type="button"
          className="header__burger"
          aria-label="Open menu"
          onClick={() => setOpen(true)}
        >
          <Menu size={22} />
        </button>
      </div>
      <Drawer open={open} onClose={() => setOpen(false)} side="right" title={t('brand.name')}>
        <nav className="header__mobile-nav" aria-label="Mobile">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `header__mobile-link${isActive ? ' header__mobile-link--active' : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="header__mobile-actions">
            <LangSwitch />
            {user ? (
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={() => { setOpen(false); navigate('/cabinet'); }}
                iconLeft={<UserIcon size={16} />}
              >
                {t('cta.cabinet')}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={() => { setOpen(false); navigate('/login'); }}
                iconLeft={<LogIn size={16} />}
              >
                {t('cta.login')}
              </Button>
            )}
            <Button variant="primary" size="lg" fullWidth onClick={handleCta}>
              {t('cta.request')}
            </Button>
          </div>
        </nav>
        <button
          type="button"
          className="header__mobile-close"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        >
          <X size={20} />
        </button>
      </Drawer>
    </header>
  );
}
